// Stage 2's card: the whole examiner report. Unlike TARA's contract, LNAT's
// examiner_report has no separate student_facing_feedback sub-object -- the
// entire document (executive_summary, dimension_feedback, ...) is prose or
// data meant to be shown, which is why lnat_scoring_service stores result_json
// and student_facing_feedback identically for this stage. feedback here is
// that whole document.

import React from 'react';
import { Prose, Heading } from './TaraFeedbackBlocks';
import { lnatDimensionLabel } from '../utils/lnatDimensionLabels';

function ScoreSnapshot({ snapshot }) {
  if (!snapshot || typeof snapshot !== 'object') return null;
  const score = typeof snapshot.overall_score === 'number' ? snapshot.overall_score : null;
  const band = snapshot.grade_band;
  const coverage = Number.isFinite(snapshot.thinking_graph_coverage)
    ? snapshot.thinking_graph_coverage : null;

  return (
    <div className="tara-fb-result">
      <div className="tara-fb-result-head">
        {band ? <span className="tara-fb-result-label">{band}</span> : null}
        <span className="tara-fb-score">
          {score === null ? '—' : score}
          <span className="tara-fb-score-of">/10</span>
        </span>
      </div>
      {coverage !== null ? (
        <Prose className="tara-fb-soft">{`Reasoning graph coverage: ${coverage}%`}</Prose>
      ) : null}
    </div>
  );
}

function DimensionRow({ entry }) {
  if (!entry || typeof entry !== 'object') return null;
  const label = lnatDimensionLabel(entry.dimension);
  const score = typeof entry.score === 'number' ? entry.score : null;
  return (
    <div className="lnat-fb-dimension">
      <div className="tara-fb-task-head">
        <span className="tara-fb-task-name">{label}</span>
        <span className="tara-fb-chip">{score === null ? 'Not scored' : `${score}/10`}</span>
      </div>
      <Prose>{entry.interpretation}</Prose>
      {entry.evidence ? <blockquote className="tara-fb-quote">{entry.evidence}</blockquote> : null}
      <Prose className="tara-fb-soft">{entry.improvement}</Prose>
    </div>
  );
}

function StringList({ items }) {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <ul className="tara-fb-list">
      {items.filter((x) => typeof x === 'string' && x.trim()).map((x, i) => <li key={i}>{x}</li>)}
    </ul>
  );
}

export default function LnatExaminerReport({ feedback }) {
  if (!feedback) return null;

  return (
    <div className="lnat-fb-report">
      <ScoreSnapshot snapshot={feedback.executive_summary_snapshot} />

      <Prose>{feedback.executive_summary}</Prose>

      <Heading>How your essay reasons</Heading>
      <Prose>{feedback.overall_reasoning_diagnosis}</Prose>

      <Heading>Reasoning graph performance</Heading>
      <Prose>{feedback.reasoning_graph_performance}</Prose>

      {Array.isArray(feedback.dimension_feedback) && feedback.dimension_feedback.length ? (
        <>
          <Heading>Dimension by dimension</Heading>
          <div className="lnat-fb-dimensions">
            {feedback.dimension_feedback.map((entry, i) => (
              <DimensionRow key={entry.dimension || i} entry={entry} />
            ))}
          </div>
        </>
      ) : null}

      {feedback.absolutism_and_hidden_tensions ? (
        <>
          <Heading>Absolutism and hidden tensions</Heading>
          <Prose>{feedback.absolutism_and_hidden_tensions}</Prose>
        </>
      ) : null}

      {Array.isArray(feedback.strengths) && feedback.strengths.length ? (
        <>
          <Heading>Strengths</Heading>
          <StringList items={feedback.strengths} />
        </>
      ) : null}

      {Array.isArray(feedback.development_areas) && feedback.development_areas.length ? (
        <>
          <Heading>Development areas</Heading>
          <StringList items={feedback.development_areas} />
        </>
      ) : null}

      {Array.isArray(feedback.recommended_next_steps) && feedback.recommended_next_steps.length ? (
        <>
          <Heading>Recommended next steps</Heading>
          <StringList items={feedback.recommended_next_steps} />
        </>
      ) : null}

      <Heading>Overall</Heading>
      <Prose>{feedback.overall_narrative}</Prose>
    </div>
  );
}
